import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faHeartPulse, faUserShield, faHandHoldingHeart, faBookMedical,
  faUsers, faBriefcaseMedical, faStethoscope, faComments, faStar
} from '@fortawesome/free-solid-svg-icons';

const Themes = ({themes}) => {
  const [hoveredIndex, setHoveredIndex] = useState(null);

  // Palette de couleurs variées pour les thèmes
  const colorPalette = [
    { 
      gradient: 'from-blue-500 to-indigo-600',
      bg: 'bg-blue-50/50',
      iconBg: 'bg-blue-100',
      text: 'text-blue-600',
      border: 'border-blue-200/50',
      shadow: 'hover:shadow-blue-500/20',
      icon: faHeartPulse,
      blob: 'bg-blue-400'
    },
    { 
      gradient: 'from-emerald-400 to-teal-600',
      bg: 'bg-emerald-50/50',
      iconBg: 'bg-emerald-100',
      text: 'text-emerald-600',
      border: 'border-emerald-200/50',
      shadow: 'hover:shadow-emerald-500/20',
      icon: faUserShield,
      blob: 'bg-emerald-400'
    },
    { 
      gradient: 'from-rose-400 to-red-500',
      bg: 'bg-rose-50/50',
      iconBg: 'bg-rose-100',
      text: 'text-rose-600',
      border: 'border-rose-200/50',
      shadow: 'hover:shadow-rose-500/20',
      icon: faHandHoldingHeart,
      blob: 'bg-rose-400'
    },
    { 
      gradient: 'from-amber-400 to-orange-500',
      bg: 'bg-amber-50/50',
      iconBg: 'bg-amber-100',
      text: 'text-amber-600',
      border: 'border-amber-200/50',
      shadow: 'hover:shadow-amber-500/20',
      icon: faBookMedical,
      blob: 'bg-amber-400'
    },
    { 
      gradient: 'from-purple-500 to-purple-700',
      bg: 'bg-purple-50/50',
      iconBg: 'bg-purple-100',
      text: 'text-purple-600',
      border: 'border-purple-200/50',
      shadow: 'hover:shadow-purple-500/20',
      icon: faUsers,
      blob: 'bg-purple-400'
    },
    { 
      gradient: 'from-cyan-400 to-blue-500',
      bg: 'bg-cyan-50/50',
      iconBg: 'bg-cyan-100',
      text: 'text-cyan-600',
      border: 'border-cyan-200/50',
      shadow: 'hover:shadow-cyan-500/20',
      icon: faBriefcaseMedical,
      blob: 'bg-cyan-400'
    },
    { 
      gradient: 'from-lime-400 to-green-600',
      bg: 'bg-lime-50/50',
      iconBg: 'bg-lime-100',
      text: 'text-lime-600',
      border: 'border-lime-200/50',
      shadow: 'hover:shadow-lime-500/20',
      icon: faStethoscope,
      blob: 'bg-lime-400'
    },
    { 
      gradient: 'from-fuchsia-400 to-pink-600',
      bg: 'bg-fuchsia-50/50',
      iconBg: 'bg-fuchsia-100',
      text: 'text-fuchsia-600',
      border: 'border-fuchsia-200/50',
      shadow: 'hover:shadow-fuchsia-500/20',
      icon: faComments,
      blob: 'bg-fuchsia-400'
    },
    { 
      gradient: 'from-yellow-400 to-amber-500',
      bg: 'bg-yellow-50/50',
      iconBg: 'bg-yellow-100',
      text: 'text-yellow-600',
      border: 'border-yellow-200/50',
      shadow: 'hover:shadow-yellow-500/20',
      icon: faStar,
      blob: 'bg-yellow-400'
    }
  ];

  if (!themes || themes.length === 0) {
    return null;
  }

  return (
    <section className="py-24 bg-gradient-to-tr from-slate-50 to-white relative overflow-hidden">
      {/* Background blobs for premium feel */}
      <div className="absolute top-10 left-10 w-96 h-96 bg-teal-100 rounded-full mix-blend-multiply filter blur-[100px] opacity-60 animate-blob"></div>
      <div className="absolute top-40 right-10 w-96 h-96 bg-blue-100 rounded-full mix-blend-multiply filter blur-[100px] opacity-60 animate-blob" style={{ animationDelay: '2s' }}></div>

      <div className="wow fadeInUp w-full relative z-10" data-wow-delay="0.1s">
        
        {/* En-tête de la section */}
        <div className="container mx-auto px-4 z-10 relative">
            <div className="text-center mb-16 wow fadeInUp" data-wow-delay="0.1s">
                <span className="text-teal-600 font-semibold tracking-wider uppercase text-sm mb-2 block">Nos Domaines d'Intervention</span>
                <h2 className="text-4xl md:text-6xl font-black text-transparent bg-clip-text bg-gradient-to-r from-teal-700 via-emerald-500 to-green-500 mb-6 tracking-tight drop-shadow-sm">
                    Thèmes Traités
                </h2>
                <div className="w-24 h-1.5 bg-gradient-to-r from-emerald-400 to-teal-500 mx-auto rounded-full mb-8"></div>
                <p className="text-lg md:text-xl text-slate-600 max-w-3xl mx-auto font-light leading-relaxed">
                    Découvrez les différents axes stratégiques sur lesquels notre organisation se concentre pour maximiser son impact.
                </p>
            </div>
            
            <div className="max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
              {themes.map((theme, index) => {
                const colors = colorPalette[index % colorPalette.length];
                const isHovered = hoveredIndex === index;

                return (
                  <div
                    key={theme.id}
                    onMouseEnter={() => setHoveredIndex(index)}
                    onMouseLeave={() => setHoveredIndex(null)}
                    className={`
                      group relative bg-white/80 backdrop-blur-xl rounded-3xl overflow-hidden
                      border border-slate-100 shadow-xl ${colors.shadow}
                      hover:-translate-y-3
                      transition-all duration-500 cursor-pointer
                      flex flex-col
                    `}
                    style={{
                      animation: `fadeInUp 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards ${index * 0.1}s`,
                      opacity: 0
                    }}
                  >
                    {/* Top Accent Gradient */}
                    <div className={`absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r ${colors.gradient} transform origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500 ease-out z-20`}></div>

                    {/* Gradient Background Glow on Hover */}
                    <div className={`absolute inset-0 bg-gradient-to-br ${colors.gradient} opacity-0 group-hover:opacity-[0.03] transition-opacity duration-500`}></div>

                    {/* Animated Decorative Blob inside Card */}
                    <div className={`
                      absolute -top-24 -right-24 w-48 h-48 
                      bg-gradient-to-br ${colors.gradient} 
                      rounded-full opacity-0 group-hover:opacity-[0.15] 
                      blur-3xl transition-all duration-700
                      group-hover:scale-150
                    `}></div>

                    <div className="relative p-8 flex flex-col flex-1 h-full">
                      {/* En-tête de la carte (Icone + Numero) */}
                      <div className="flex justify-between items-start mb-6">
                        <div className={`
                          w-16 h-16 rounded-2xl bg-gradient-to-br ${colors.gradient}
                          flex items-center justify-center text-white
                          group-hover:scale-110 group-hover:-rotate-3 group-hover:shadow-lg
                          transition-all duration-500 shadow-md relative z-10
                        `}>
                          {/* Inner soft glow */}
                          <div className="absolute inset-0 bg-white/20 rounded-2xl mix-blend-overlay"></div>
                          <FontAwesomeIcon icon={colors.icon} className="text-3xl drop-shadow-md" />
                        </div>

                        {/* Numéro avec style premium */}
                        <div className={`
                          w-10 h-10 rounded-full bg-slate-50 border border-slate-100 text-slate-400 font-bold
                          flex items-center justify-center text-sm shadow-sm
                          group-hover:opacity-0
                          transition-all duration-500
                        `}>
                          {String(index + 1).padStart(2, '0')}
                        </div>

                        {/* Numéro version colorée au survol (superposé) */}
                        <div className={`
                          absolute right-8 top-8 w-10 h-10 rounded-full bg-gradient-to-br ${colors.gradient} text-white font-bold
                          flex items-center justify-center text-sm shadow-md
                          opacity-0 group-hover:opacity-100 transform scale-50 group-hover:scale-100
                          transition-all duration-500
                        `}>
                          {String(index + 1).padStart(2, '0')}
                        </div>
                      </div>

                      {/* Title */}
                      <h4 className={`
                        text-xl font-extrabold text-slate-800 mb-4 leading-snug tracking-tight
                        group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:${colors.gradient}
                        transition-all duration-500
                      `}>
                        {theme.title}
                      </h4>

                      {/* Description */}
                      {theme.description && (
                        <p className="text-slate-500 font-medium text-sm leading-relaxed line-clamp-3 mb-6 flex-grow">
                          {theme.description}
                        </p>
                      )}

                      {/* Bottom action */}
                      <div className="flex items-center mt-auto pt-5 mt-4 border-t border-slate-100/80">
                        <span className={`text-sm font-bold text-slate-400 group-hover:${colors.text} transition-colors duration-300 flex-1`}>
                          En savoir plus
                        </span>
                        <div className={`
                          w-10 h-10 rounded-full bg-slate-50 text-slate-400 border border-slate-100
                          flex items-center justify-center
                          group-hover:bg-transparent group-hover:shadow-none group-hover:border-transparent group-hover:translate-x-2
                          transition-all duration-300
                        `}>
                          <svg className={`w-5 h-5 group-hover:${colors.text} transition-colors duration-300`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .line-clamp-3 {
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </section>
  );
};

export default Themes;